import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import logo from "./assets/logo.png";

// Import icons from lucide-react
import { useAuth } from "contexts/AuthContext";
import { useTranslation } from "contexts/i18nContext";
import { useHasRole } from "contexts/AuthContext";
import {
  BarChart3,
  FileText,
  Gavel,
  LayoutDashboard,
  MessageCircleWarning,
  Users,
} from "lucide-react";
import { fetchAdminUnitDetail } from "services/advocateComplaintService";
import { AdminUnit } from "types";
import { ADVOCATE_ROLES, ROLES } from "utils/roles";

type SideBarPropes = {
  isInternalUser: boolean;
};

const Sidebar: React.FC<SideBarPropes> = ({ isInternalUser }) => {
  const location = useLocation();
  const { user } = useAuth();
  const { t } = useTranslation();

  const canViewComplaint = useHasRole([
    ROLES.INSPECTION_COORDINATOR,
    ROLES.INSPECTION_PROSECUTOR,
    ROLES.INSPECTION_REGISTRAR,
    ROLES.INSPECTION_DIRECTOR,
    ROLES.REGIONAL_COUNCIL_OFFICE_HEAD,
    ROLES.REGIONAL_COUNCIL_OFFICE_WRITER,
    ROLES.ZONAL_COUNCIL_OFFICE_HEAD,
    ROLES.ZONAL_COUNCIL_OFFICE_WRITER,
    ADVOCATE_ROLES.INSPECTION_REGISTRAR,
  ]);

  const canViewAdvocate = useHasRole([
    ADVOCATE_ROLES.INSPECTION_COORDINATOR,
    ADVOCATE_ROLES.INSPECTION_PROSECUTOR,
    ADVOCATE_ROLES.INSPECTION_REGISTRAR,
    ADVOCATE_ROLES.INSPECTION_DIRECTOR,
    ADVOCATE_ROLES.ZONAL_COUNCIL_OFFICE_HEAD,
    ADVOCATE_ROLES.REGIONAL_COUNCIL_OFFICE_HEAD,
    ADVOCATE_ROLES.ZONAL_COUNCIL_OFFICE_WRITER,
    ADVOCATE_ROLES.REGIONAL_COUNCIL_OFFICE_WRITER,
    ADVOCATE_ROLES.ZONAL_OFFICE_OF_COUNCIL_COORDINATOR,
    ADVOCATE_ROLES.ZONAL_COUNCIL_OFFICE_WRITER,
    ROLES.INSPECTION_REGISTRAR,
  ]);

  const isProsecutor = useHasRole([ROLES.INSPECTION_PROSECUTOR]);

  const [adminUnitData, setAdminUnitData] = useState<AdminUnit>();
  const [isAdminUnitLoading, setIsAdminUnitLoading] = useState(true);

  useEffect(() => {
    const loadInitialPageData = async () => {
      setIsAdminUnitLoading(true);
      try {
        const adminUnitDetail = await fetchAdminUnitDetail(
          user?.admin_unit,
          user?.access,
        ).catch(() => undefined);
        setAdminUnitData(adminUnitDetail);
      } catch (err) {
        setAdminUnitData(undefined);
      } finally {
        setIsAdminUnitLoading(false);
      }
    };

    loadInitialPageData();
  }, [user]);

  const canViewMobile = useHasRole([
    ROLES.INSPECTION_COORDINATOR,
    ADVOCATE_ROLES.INSPECTION_COORDINATOR,
  ]);

  const showProsecutorAppealMenus =
    !isProsecutor || (!isAdminUnitLoading && !adminUnitData?.parent_unit);

  const navItem = (title: string, path: string, Icon: React.ElementType) => {
    const isActive =
      path === "/"
        ? location.pathname === "/"
        : location.pathname.startsWith(path);

    return (
      <Link to={path}>
        <div
          className={`flex items-center gap-2 px-3 py-2 rounded font-medium ${
            isActive ? "bg-white text-black" : "hover:bg-gray-800"
          }`}
        >
          <Icon size={16} />
          {title}
        </div>
      </Link>
    );
  };

  return (
    <aside className="flex-col justify-between hidden w-64 min-h-screen text-white bg-black md:flex">
      <div className="p-4">
        <div className="flex items-center gap-2 mb-4">
          <img src={logo} alt="Logo" className="w-12 h-10 rounded" />
          <div className="text-xs leading-tight">
            <div className="text-sm font-semibold">{t("brandOfficeName")}</div>
            <div className="text-gray-400 text-[11px]">
              {t("brandOfficeNameAm")}
            </div>
          </div>
        </div>

        <nav className="flex flex-col space-y-1 text-sm">
          <Section title={t("menuDashboardSection")}>
            {navItem(t("menuDashboard"), "/", LayoutDashboard)}
            {/* {navItem(t("menuMainTask"), "/main-task", ListTodo)} */}
            {navItem(t("menuReports"), "/reports", BarChart3)}
          </Section>
          {canViewComplaint && (
            <Section title={t("menuProsecutor")}>
              {navItem(t("menuComplaint"), "/complaint-request", FileText)}
              {navItem(
                t("menuAppealedComplaint"),
                "/appeal-request",
                MessageCircleWarning,
              )}
              {showProsecutorAppealMenus
                ? navItem(t("menuAccusedAppeal"), "/accused-complaint", Users)
                : null}
              {showProsecutorAppealMenus
                ? navItem(t("menuCourtAppeal"), "/court-appeal", Gavel)
                : null}
              {isProsecutor && isAdminUnitLoading && (
                <div className="flex items-center gap-2 px-3 py-2 text-xs text-gray-400">
                  <div className="h-3 w-3 border border-gray-500 border-t-transparent rounded-full animate-spin" />
                  {t("loadingMenus")}
                </div>
              )}
              {/* {navItem(
              "Activity Logs",
              "/activity-logs/prosecutor",
              FolderSearch
            )} */}
            </Section>
          )}

          {canViewAdvocate && (
            <Section title={t("menuAdvocate")}>
              {navItem(t("menuCompliant"), "/advocate-complaint", FileText)}
              {navItem(
                t("menuAppealedCompliant"),
                "/advocate-appeal",
                MessageCircleWarning,
              )}
              {navItem(
                t("menuAccusedAppeal"),
                "/advocate-accused-appeal",
                Users,
              )}
              {navItem(t("menuCourtAppeal"), "/advocate-court-appeal", Gavel)}
              {/* {navItem("Activity Logs", "/activity-logs/advocate", FolderSearch)} */}
            </Section>
          )}
          {canViewMobile && (
            <Section title={t("menuDiscipline")}>
              {navItem(t("menuReportByMobile"), "/report-by-mobile", BarChart3)}
            </Section>
          )}
        </nav>
      </div>
      <div className="p-4 text-xs text-gray-400">{t("poweredByEaii")}</div>
    </aside>
  );
};

export default Sidebar;

function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="mt-4">
      <div className="mb-2 text-xs font-semibold text-gray-400 uppercase">
        {title}
      </div>
      <div className="space-y-1">{children}</div>
    </div>
  );
}
